import {
  FEATURED_INDUSTRIES,
  FALLBACK_REMAINING_INDUSTRIES,
  FALLBACK_LOCATIONS,
  OTHER_INDUSTRY,
} from "../constants";
import { STEP_ACTIONS, STEP_CRM, STEP_INDUSTRY, STEP_LOCATION, STEP_REVENUE, STEP_NAME } from "../onboardingShared";
import { CRM_SUGGESTIONS } from "./crmStep";
import { ACTION_STEP_SUGGESTIONS } from "./actionStep";
import { REVENUE_MAX, REVENUE_MIN } from "../../../helper/rangeFilter";

/** Conversational copy + suggestion metadata for each chat onboarding question. */
export const CRM_CHAT_QUESTION = {
  id: "crm", step: STEP_CRM, prompt: "Which CRM do you use?",
  description: "Select the CRM platforms you use to personalize your workflow. This does not connect an account.",
  clarification: "Choose HubSpot, Salesforce, DealCloud, Other with its name, or I don't use a CRM.",
  placeholder: "Choose your CRMs…", otherPlaceholder: "Enter the CRM your team uses...",
  selection: "multiple", kind: "crm", suggestions: CRM_SUGGESTIONS,
};

export const CHAT_QUESTIONS = [
  {
    id: "industry",
    step: STEP_INDUSTRY,
    prompt: "What types of companies are you primarily interested in?",
    clarification:
      "I didn’t quite catch that. Could you choose one of the industries below, or name the industries you’re most interested in?",
    placeholder: "Type an industry, or pick a suggestion…",
    selection: "multiple",
    suggestions: [...FEATURED_INDUSTRIES, OTHER_INDUSTRY],
  },
  {
    id: "location",
    step: STEP_LOCATION,
    prompt: "Where should those companies be headquartered?",
    clarification:
      "I’m not sure that answers this question. Could you tell me which locations you’re most interested in?",
    placeholder: "Type a location, or pick a suggestion…",
    selection: "multiple",
    // Full location catalog is supplied at runtime from form-step options / API.
    suggestions: FALLBACK_LOCATIONS,
  },
  {
    id: "revenue",
    step: STEP_REVENUE,
    prompt: "What revenue range are you looking for?",
    clarification:
      "I didn’t quite understand that revenue range. Try something like “$10M–$50M”, “under $5M”, or pick a suggestion below.",
    placeholder: "e.g. $10M to $50M, or “any size”…",
    selection: "single",
    suggestions: [
      { label: "Any size", value: { revenueLowerBound: null, revenueUpperBound: null } },
      {
        label: "Under $5M",
        value: { revenueLowerBound: null, revenueUpperBound: 5_000_000 },
      },
      {
        label: "$5M – $25M",
        value: { revenueLowerBound: 5_000_000, revenueUpperBound: 25_000_000 },
      },
      {
        label: "$25M – $100M",
        value: { revenueLowerBound: 25_000_000, revenueUpperBound: 100_000_000 },
      },
      {
        label: "$100M+",
        value: { revenueLowerBound: 100_000_000, revenueUpperBound: null },
      },
    ],
  },
  {
    id: "actions",
    step: STEP_ACTIONS,
    prompt: "What would you like to learn about your companies?",
    clarification:
      "Pick any of the actions below, or choose “Continue without actions”. We run them on the first 5 companies of your search.",
    placeholder: "Pick actions to run on your first 5 companies…",
    selection: "multiple",
    suggestions: ACTION_STEP_SUGGESTIONS,
  },
  CRM_CHAT_QUESTION,
  {
    id: "searchName",
    step: STEP_NAME,
    prompt: "What would you like to call this first search?",
    clarification: "Could you share a short name for this search, or use the suggestion below?",
    placeholder: "Name your search…",
    selection: "text",
    suggestions: [], // filled dynamically from suggestSearchName
  },
];

export const getChatQuestion = (step) =>
  CHAT_QUESTIONS.find((question) => question.step === step) || CHAT_QUESTIONS[0];

export const ALL_INDUSTRY_OPTIONS = [
  ...FEATURED_INDUSTRIES,
  ...FALLBACK_REMAINING_INDUSTRIES,
];

export const ALL_LOCATION_OPTIONS = FALLBACK_LOCATIONS;

export const formatRevenueDisplay = (lowerBound, upperBound) => {
  const format = (value) => {
    if (value == null) return null;
    if (value >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(value % 1_000_000_000 === 0 ? 0 : 1)}B`;
    if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(value % 1_000_000 === 0 ? 0 : 1)}M`;
    if (value >= 1_000) return `$${(value / 1_000).toFixed(value % 1_000 === 0 ? 0 : 1)}K`;
    return `$${value}`;
  };

  const lower = format(lowerBound);
  const upper = format(upperBound);

  if (!lower && !upper) return "Any size";
  if (!lower && upper) return `Under ${upper}`;
  if (lower && !upper) return `${lower}+`;
  return `${lower} – ${upper}`;
};

export const isFullRevenueRange = (lowerBound, upperBound) => {
  const atMin = lowerBound == null || lowerBound <= REVENUE_MIN;
  const atMax = upperBound == null || upperBound >= REVENUE_MAX;
  return atMin && atMax;
};
